import { useAtomValue } from 'jotai';

import { filtersListAtom as plpFiltersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import type { DealershipAttrs } from '@core/Entities/Dealership/Dealership.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import { useIsExperimentEnabled } from '@growthBookExperimentation/Hooks/useIsExperimetEnabled';
import { useModal } from '@modal/Hooks/UseModal';
import { generateSavedSearchTrackingData } from '@tracking/SavedSearch.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import type { LocationAttrs } from 'Components/SearchWidget/Search.entity';

import type { SavedSearchStrings } from './SavedSearch.entity';
import { SavedSearchForm } from './SavedSearchForm';

import styles from './SavedSearchForm.module.css';

interface UseSavedSearchForm {
  locale: Locale;
  strings: SavedSearchStrings;
  locationAttrs?: LocationAttrs;
  dealerAttrs?: DealershipAttrs;
  defaultFilters?: Filter[];
}

export const useSavedSearchForm = ({
  locale,
  strings,
  locationAttrs,
  dealerAttrs,
  defaultFilters,
}: UseSavedSearchForm) => {
  const userId = useAtomValue(userIdAtom);
  const { openModal, resetModal } = useModal();
  const plpFilters = useAtomValue(plpFiltersListAtom);

  const filters = defaultFilters ?? plpFilters;
  const displayFilters = mapFiltersForDisplay(filters);

  const isSingleFilterOn = useIsExperimentEnabled('saved-search-single-filter-impact', '1');
  const filterLengthGte = isSingleFilterOn ? 0 : 1;

  const canUseSavedSearch = displayFilters.length > filterLengthGte;

  const {
    createAlertStart: { event, context },
  } = generateSavedSearchTrackingData({
    pageFilters: filters,
    userId,
  });

  const openForm = () => {
    trackCustomEvent({ event, context });
    openModal(
      <SavedSearchForm
        locale={locale}
        strings={strings}
        locationAttrs={locationAttrs}
        dealerAttrs={dealerAttrs}
        allFilters={filters}
      />,
      {
        header: strings.createAlert,
        modalClassName: styles.modalContainer,
        onClose: () => resetModal(),
      },
    );
  };

  return {
    openForm,
    canUseSavedSearch,
  };
};
